import * as Sentry from '@sentry/react';
import {getAuth} from 'firebase/auth';
import geojson from 'geojson';
import * as I from 'immutable';
import Protobuf from 'pbf';
import vt from 'vector-tile';

import {ApiFeatureCollection} from 'app/modules/Remote/FeatureCollection';
import {type Tile, getTileData, limitedParallelAll} from 'app/stores/RasterCalculationStore';
import * as apiUtils from 'app/utils/apiUtils';

import {formatTileUrl} from '.';

const TILE_FETCH_PARALLELISM = 8;
const TILE_RATE_LIMIT_RETRY_DELAYS_MS = [1_000, 2_000, 4_000];

export default async function loadTiledFeatureCollectionAsGeoJSON(
  featureCollection: I.ImmutableOf<ApiFeatureCollection>,
  filterGeometry: geojson.Polygon | geojson.MultiPolygon,
  options: {maxZoom?: number; signal?: AbortSignal} = {}
): Promise<geojson.FeatureCollection | null> {
  if (!featureCollection.get('tiles')) {
    return null;
  }

  const tileData = getTileData(filterGeometry, options);
  if (!tileData) {
    return null;
  }

  const auth = getAuth();
  const token = auth.currentUser ? await auth.currentUser.getIdToken() : null;

  const urlTemplate: string = featureCollection.getIn(['tiles', 'urls']).first();
  const sourceLayer: string = featureCollection.getIn(['tiles', 'sourceLayer']);
  const featureCollectionId: number = featureCollection.get('id');

  const tileResults = await limitedParallelAll(
    tileData.tiles.map(
      (tile) => () =>
        fetchTileFeatures(tile, {
          urlTemplate,
          sourceLayer,
          featureCollectionId,
          token,
          signal: options.signal,
        })
    ),
    TILE_FETCH_PARALLELISM
  );

  return {
    type: 'FeatureCollection',
    features: tileResults.flat(),
  };
}

async function fetchTileFeatures(
  tile: Tile,
  ctx: {
    urlTemplate: string;
    sourceLayer: string;
    featureCollectionId: number;
    token: string | null;
    signal?: AbortSignal;
  }
): Promise<geojson.Feature[]> {
  let url = ctx.urlTemplate
    .replace('{x}', tile[0].toString())
    .replace('{y}', tile[1].toString())
    .replace('{z}', tile[2].toString());

  // feature collections will define their tiles as relative paths when they are internal,
  // i.e. regrid/tiles/12/1245/1522.mvt
  if (!url.startsWith('http')) {
    url = apiUtils.getApiRoute(url);
  }

  if (ctx.token && url.indexOf(apiUtils.getApiRoot()) > -1) {
    url = formatTileUrl(url, ctx.token);
  }

  try {
    const response = await fetchWithRateLimitRetry(url, {signal: ctx.signal});
    if (!response.ok) {
      throw new Error(`Tile fetch failed with status ${response.status}`);
    }
    const buffer = await response.arrayBuffer();
    return readTile(new Uint8Array(buffer), ctx.sourceLayer, tile).features;
  } catch (e) {
    // Re-throw aborts so callers can short-circuit cleanup.
    if (e instanceof DOMException && e.name === 'AbortError') {
      throw e;
    }
    Sentry.captureException(e, {
      tags: {component: 'loadTiledFeatureCollectionAsGeoJSON'},
      extra: {
        tileXYZ: tile,
        sourceLayer: ctx.sourceLayer,
        featureCollectionId: ctx.featureCollectionId,
        url,
      },
    });
    return [];
  }
}

// Exported for testing.
export async function fetchWithRateLimitRetry(url: string, init: RequestInit): Promise<Response> {
  for (let i = 0; i < TILE_RATE_LIMIT_RETRY_DELAYS_MS.length; i++) {
    const response = await fetch(url, init);
    if (response.ok || response.status !== 429) return response;
    await new Promise<void>((resolve) => setTimeout(resolve, TILE_RATE_LIMIT_RETRY_DELAYS_MS[i]));
  }
  return fetch(url, init);
}

function readTile(
  buffer: Uint8Array,
  layer: string,
  tileXYZ: [number, number, number]
): geojson.FeatureCollection {
  const tile = new vt.VectorTile(new Protobuf(buffer));
  let layers = layer || Object.keys(tile.layers);
  if (!Array.isArray(layers)) {
    layers = [layers];
  }

  const fc: geojson.FeatureCollection = {type: 'FeatureCollection', features: []};
  const [x, y, z] = tileXYZ;

  layers.forEach(function (layerID) {
    const layer = tile.layers[layerID];
    if (layer) {
      for (let i = 0; i < layer.length; i++) {
        const feature = layer.feature(i).toGeoJSON(x, y, z);
        if (layers.length > 1) feature.properties.vt_layer = layerID;
        fc.features.push(feature);
      }
    }
  });

  return fc;
}
